/**
 * Central org-change navigation registry.
 *
 * Every Capillary module ships the same org-change behaviour (cross-tab
 * `storage` listener on `orgID` -> refresh modal + countdown -> navigate). The
 * only per-module difference is where the module navigates after the org
 * switches. This registry combines that per-module navigation so the intouch
 * shell can dispatch the correct one based on the running module's publicPath.
 *
 * Value per publicPath:
 *   - RELOAD                -> reload the current page
 *   - string                -> pathname to redirect to (origin is prefixed)
 *   - (pathname) => string  -> compute the pathname from the current location
 *
 * Targets are the canonical (production) landing path for each module.
 */

export const RELOAD = 'RELOAD';

export const ORG_CHANGE_REGISTRY = {
  '/home/ui': RELOAD,
  '/ask-aira/ui': RELOAD,
  '/launchpad/ui': '/launchpad/ui',
  '/extensions/neo/ui': '/extensions/neo/ui',
  '/extensions/connectplus/ui': '/extensions/connectplus/ui',
  '/vulcan/ui': '/vulcan/ui',
  '/events/ui': '/events/ui',
  '/audience-manager': '/audience-manager/list',
  '/coupons/ui': '/coupons/ui',
  '/creatives/ui': '/creatives/ui/v2',
  '/badges/ui': '/badges/ui',
  '/analytics/v2': '/analytics/v2/reports',
  '/campaigns/ui': '/campaigns/ui/list',
  '/journey/ui': '/journey/ui',
  '/loyalty/ui/v3': '/loyalty/ui/v3/',
  '/loyalty/ui': '/loyalty/ui',
  '/rewards-catalog/ui': '/rewards-catalog/ui',
  '/import/ui': '/import/ui',
  '/tesseract/ui': pathname =>
    pathname.includes('streaks')
      ? '/tesseract/ui/streaks'
      : '/tesseract/ui/milestones',
  '/member-care/ui': '/member-care/ui',
  '/cards-configuration/ui': '/cards-configuration/ui',
  // /customer-status-configuration/ui is deliberately absent: the standalone
  // page was retired for the embedded Customers tab, and the host router now
  // redirects the old URL (Cap/routes.js) — the RELOAD fallback covers it.
  '/events-notification/ui': '/events-notification/ui',
  '/hierarchy/ui': '/hierarchy/ui',
  '/search-criteria/ui': '/search-criteria/ui',
};

// publicPaths sorted longest-first so the most specific prefix wins
// (e.g. /loyalty/ui/v3 resolves to garuda, not loyalty).
const MATCH_KEYS = Object.keys(ORG_CHANGE_REGISTRY).sort(
  (a, b) => b.length - a.length,
);

const matches = (pathname, key) =>
  pathname === key || pathname.startsWith(`${key}/`);

/**
 * Resolve the org-change navigation handler for a given pathname.
 * Falls back to a full page reload when no module matches.
 * @param {string} [pathname=window.location.pathname]
 * @returns {() => void} a navigateToDashboard-style function
 */
export const matchOrgChangeHandler = (
  pathname = window.location.pathname,
) => () => {
  const key = MATCH_KEYS.find(path => matches(pathname, path));
  const target = key && ORG_CHANGE_REGISTRY[key];
  if (!target || target === RELOAD) {
    window.location.reload();
    return;
  }
  const path = typeof target === 'function' ? target(pathname) : target;
  window.location.href = `${window.location.origin}${path}`;
};
